import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuChevronLeft,
  LuChevronRight,
  LuEllipsisVertical,
  LuEye,
  LuPencil,
  LuPlus,
  LuRefreshCw,
  LuSearch,
  LuUserPlus,
  LuUsers,
  LuUserCheck,
  LuUserMinus,
  LuMail,
  LuPower,
  LuSend,
  LuTrash2,
} from "react-icons/lu";
import {
  accessExtractMessage,
  deleteAccessUser,
  fetchAccessRoles,
  fetchAccessUserById,
  fetchAccessUsers,
  resendAccessUserInvitation,
  resetAccessUserPassword,
  updateAccessUserAvailability,
  updateAccessUser,
} from "../../services/superAdminAccess.service";
import "../../assets/styles/super_admin_access.scss";

const PAGE_OPTIONS = [5, 10, 20];

const initials = (user) =>
  `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "U";

export default function SuperAdminUserManagementPage({ basePath = "/super-admin" }) {
  const isSupportManager = basePath.includes("/support-manager");
  const isLogisticsManager = basePath.includes("/logistics-manager");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState(() => searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);
  const [viewUser, setViewUser] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAccessUsers({
        search: query.trim() || undefined,
        roleId: role === "all" ? undefined : role,
        status: status === "all" ? undefined : status,
      });
      setUsers(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to load users."));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [query, role, status]);

  useEffect(() => {
    const nextStatus = searchParams.get("status") || "all";
    setStatus(nextStatus);
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAccessRoles({ status: "Active" });
        if (!cancelled) setRoles(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!cancelled) setRoles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadUsers]);

  useEffect(() => {
    if (openMenuId == null) return undefined;

    const closeMenu = () => {
      setOpenMenuId(null);
      setMenuPosition(null);
    };
    const onPointerDown = (event) => {
      const target = event.target;
      if (
        !(target instanceof Element) ||
        !target.closest(`[data-user-menu="${openMenuId}"]`)
      ) {
        closeMenu();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [openMenuId]);

  const filtered = users;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  const openMenuUser = users.find((user) => user.id === openMenuId) || null;

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((item) => item.status === "Active").length;
    const pending = users.filter((item) => item.status === "Pending").length;
    const inactive = users.filter((item) => item.status === "Inactive").length;
    return { total, active, pending, inactive };
  }, [users]);

  const resetFilters = () => {
    setQuery("");
    setRole("all");
    setStatus("all");
    setPage(1);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const toggleMoreMenu = (event, userId) => {
    if (openMenuId === userId) {
      closeMenu();
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const panelHeight = 145;
    const panelWidth = 210;
    const gap = 6;
    const fitsBelow = window.innerHeight - rect.bottom >= panelHeight + gap;
    setMenuPosition({
      top: fitsBelow
        ? rect.bottom + gap
        : Math.max(8, rect.top - panelHeight - gap),
      left: Math.min(
        window.innerWidth - panelWidth - 8,
        Math.max(8, rect.right - panelWidth),
      ),
    });
    setOpenMenuId(userId);
  };

  const handleViewDetails = async (user) => {
    closeMenu();
    setBusyUserId(user.id);
    try {
      const fullUser = await fetchAccessUserById(user.id);
      setViewUser(fullUser || user);
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to load user details."));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleResendInvitation = async (user) => {
    closeMenu();
    setBusyUserId(user.id);
    try {
      await resendAccessUserInvitation(user.id);
      toast.success(`Invitation sent to ${user.email}.`);
      await loadUsers();
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to resend invitation."));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleToggleStatus = async (user) => {
    closeMenu();
    const nextStatus = user.status === "Active" ? "Inactive" : "Active";
    setBusyUserId(user.id);
    try {
      await updateAccessUser(user.id, { status: nextStatus });
      toast.success(
        nextStatus === "Active" ? "User activated." : "User deactivated.",
      );
      await loadUsers();
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to update user status."));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleResetPassword = async (user) => {
    closeMenu();
    setBusyUserId(user.id);
    try {
      await resetAccessUserPassword(user.id);
      toast.success(`Temporary password sent to ${user.email}.`);
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to reset password."));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleToggleAvailability = async (user) => {
    closeMenu();
    const next =
      user.availabilityStatus === "unavailable" ? "available" : "unavailable";
    setBusyUserId(user.id);
    try {
      await updateAccessUserAvailability(user.id, next);
      toast.success(`Support User marked ${next}.`);
      await loadUsers();
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to update availability."));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDeleteUser = async (user) => {
    closeMenu();
    const confirmed = window.confirm(
      `Delete user "${user.firstName} ${user.lastName}"? Their platform access will be removed.`,
    );
    if (!confirmed) return;

    setBusyUserId(user.id);
    try {
      await deleteAccessUser(user.id);
      toast.success("User deleted successfully.");
      if (viewUser?.id === user.id) setViewUser(null);
      await loadUsers();
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to delete user."));
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="sa-access-page">
      <header className="sa-access-head">
        <div>
          <h1>{isLogisticsManager ? "Logistics Users" : "User Management"}</h1>
          <p>
            {isLogisticsManager
              ? "Manage logistics team members, roles, and portal access."
              : "Manage platform users, their roles and access."}
          </p>
        </div>
        <div className="sa-access-actions">
          <Link to={`${basePath}/users/create`} className="sa-access-btn sa-access-btn--primary">
            <LuPlus aria-hidden />{" "}
            {isLogisticsManager ? "Create Logistics User" : "Create User"}
          </Link>
        </div>
      </header>

      <section className="sa-access-stats">
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--blue"><LuUsers /></span>
          <div>
            <strong>{stats.total.toLocaleString()}</strong>
            <span>Total Users</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--green"><LuUserCheck /></span>
          <div>
            <strong>{stats.active.toLocaleString()}</strong>
            <span>Active Users</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--orange"><LuMail /></span>
          <div>
            <strong>{stats.pending.toLocaleString()}</strong>
            <span>Pending Invitations</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--red"><LuUserMinus /></span>
          <div>
            <strong>{stats.inactive.toLocaleString()}</strong>
            <span>Inactive Users</span>
          </div>
        </article>
      </section>

      <div
        className="sa-access-toolbar"
        style={{
          gridTemplateColumns:
            "minmax(0, 1.4fr) repeat(2, minmax(140px, 180px)) auto",
        }}
      >
        <label className="sa-access-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search by name, email or mobile..."
          />
        </label>
        <select value={role} onChange={(event) => { setRole(event.target.value); setPage(1); }}>
          <option value="all">All Roles</option>
          {roles.map((item) => (
            <option key={item.id} value={item.id}>{item.name}</option>
          ))}
        </select>
        <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
          <option value="all">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Pending">Pending</option>
        </select>
        <div className="sa-access-actions">
          <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={resetFilters}>
            <LuRefreshCw aria-hidden /> Reset
          </button>
          <Link to={`${basePath}/users/create`} className="sa-access-btn sa-access-btn--primary">
            <LuUserPlus aria-hidden />{" "}
            {isLogisticsManager ? "Create Logistics User" : "Create User"}
          </Link>
        </div>
      </div>

      <section className="sa-access-card">
        <div style={{ overflowX: "auto" }}>
          <table className="sa-access-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Company</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Workload</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="sa-access-empty">Loading users...</td>
                </tr>
              ) : pageRows.length ? (
                pageRows.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="sa-access-user">
                        <span className="sa-access-avatar">{initials(user)}</span>
                        <div>
                          <strong>{user.firstName} {user.lastName}</strong>
                          <small>ID: {user.userCode || user.id}</small>
                        </div>
                      </div>
                    </td>
                    <td>{user.company}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`sa-access-badge sa-access-badge--${user.roleTone || "orange"}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`sa-access-badge sa-access-badge--${
                          user.status === "Active" ? "green" : user.status === "Pending" ? "yellow" : "red"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td>
                      {user.workload ? (
                        <span title={`${user.workload.assigned} total assigned`}>
                          {user.workload.active} active
                          {user.workload.overdue ? ` · ${user.workload.overdue} overdue` : ""}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{user.lastLogin}</td>
                    <td>
                      <div className="sa-access-row-actions">
                        <button
                          type="button"
                          title="View"
                          disabled={busyUserId === user.id}
                          onClick={() => handleViewDetails(user)}
                        >
                          <LuEye />
                        </button>
                        <Link to={`${basePath}/users/${user.id}/edit`} title="Edit"><LuPencil /></Link>
                        <button
                          type="button"
                          title="More actions"
                          data-user-menu={user.id}
                          disabled={busyUserId === user.id}
                          onClick={(event) => toggleMoreMenu(event, user.id)}
                        >
                          <LuEllipsisVertical />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="sa-access-empty">No users matched your filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="sa-access-footer">
          <span>
            Showing {filtered.length ? start + 1 : 0} to {Math.min(start + pageSize, filtered.length)} of{" "}
            {filtered.length} users
          </span>
          <div className="sa-access-pagination">
            <button type="button" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}>
              <LuChevronLeft />
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1)
              .slice(0, 5)
              .map((item) => (
                <button
                  key={item}
                  type="button"
                  className={currentPage === item ? "is-active" : ""}
                  onClick={() => setPage(item)}
                >
                  {item}
                </button>
              ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <LuChevronRight />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} / page</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {openMenuUser && menuPosition
        ? createPortal(
            <div
              className="sa-access-more__panel sa-access-more__panel--portal"
              data-user-menu={openMenuUser.id}
              role="menu"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => handleResendInvitation(openMenuUser)}
              >
                <LuSend aria-hidden /> Resend Invitation
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => handleToggleStatus(openMenuUser)}
              >
                <LuPower aria-hidden />{" "}
                {openMenuUser.status === "Active" ? "Deactivate" : "Activate"}
              </button>
              {isSupportManager ? (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleToggleAvailability(openMenuUser)}
                  >
                    {openMenuUser.availabilityStatus === "unavailable" ? (
                      <LuUserCheck aria-hidden />
                    ) : (
                      <LuUserMinus aria-hidden />
                    )}{" "}
                    {openMenuUser.availabilityStatus === "unavailable"
                      ? "Mark Available"
                      : "Mark Unavailable"}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleResetPassword(openMenuUser)}
                  >
                    <LuRefreshCw aria-hidden /> Reset Password
                  </button>
                </>
              ) : null}
              {isLogisticsManager ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleResetPassword(openMenuUser)}
                >
                  <LuRefreshCw aria-hidden /> Reset Password
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                className="sa-access-more__danger"
                onClick={() => handleDeleteUser(openMenuUser)}
              >
                <LuTrash2 aria-hidden /> Delete User
              </button>
            </div>,
            document.body,
          )
        : null}

      {viewUser ? (
        <div
          className="sa-access-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sa-user-view-title"
          onClick={() => setViewUser(null)}
        >
          <div
            className="sa-access-modal__panel"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sa-access-modal__head">
              <div>
                <h2 id="sa-user-view-title">
                  {viewUser.firstName} {viewUser.lastName}
                </h2>
                <p>{viewUser.email}</p>
              </div>
              <button
                type="button"
                className="sa-access-btn sa-access-btn--ghost"
                onClick={() => setViewUser(null)}
              >
                Close
              </button>
            </div>
            <div className="sa-access-modal__grid">
              <div>
                <span>User ID</span>
                <strong>{viewUser.userCode || viewUser.id}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{viewUser.status}</strong>
              </div>
              <div>
                <span>Role</span>
                <strong>{viewUser.role || "—"}</strong>
              </div>
              <div>
                <span>Mobile</span>
                <strong>{viewUser.mobile || "—"}</strong>
              </div>
              <div>
                <span>Company</span>
                <strong>{viewUser.company || "—"}</strong>
              </div>
              <div>
                <span>Department</span>
                <strong>{viewUser.department || "—"}</strong>
              </div>
              <div>
                <span>Designation</span>
                <strong>{viewUser.designation || "—"}</strong>
              </div>
              <div>
                <span>Last Login</span>
                <strong>{viewUser.lastLogin || "—"}</strong>
              </div>
              <div>
                <span>Location</span>
                <strong>
                  {[viewUser.city, viewUser.state, viewUser.country]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </strong>
              </div>
              <div>
                <span>Invitation Sent</span>
                <strong>
                  {viewUser.invitationSentAt
                    ? new Date(viewUser.invitationSentAt).toLocaleString()
                    : "—"}
                </strong>
              </div>
            </div>
            <div className="sa-access-modal__footer">
              <button
                type="button"
                className="sa-access-btn sa-access-btn--ghost"
                onClick={() => setViewUser(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="sa-access-btn sa-access-btn--primary"
                onClick={() =>
                  navigate(`${basePath}/users/${viewUser.id}/edit`)
                }
              >
                Edit User
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
